import {
  createEntityAdapter,
  createSlice,
  PayloadAction,
  Update,
} from '@reduxjs/toolkit'
import { StoreData } from '../reducers/typings'
import { IOpportunity, OpportunityPageState } from './typings'
import {
  bulkCloseWinOpportunities,
  bulkDeleteOpps,
  bulkReassignOpps,
  bulkReopenOpps,
  bulkUpdateExpectedClosingDate,
  bulkUpdateNextActionDate,
  bulkUpdateOppStatus,
  closeWinOpportunity,
  createOpportunity,
  deleteOpportunity,
  getOpportunities,
  getOpportunity,
  getOpportunityDetail,
  getOpportunityIsCloseable,
  getOpportunityValue,
  hasMultiServiceLocations,
  incrementOpportunityPaymentsCount,
  reOpenOpportunity,
  splitLeadOrOpportunity,
  updateName,
  updateOpportunityExpectedClosingDate,
  updateOpportunityInfo,
  updateOpportunityNextActionDate,
  updateOpportunityStatus,
  updateReassignOpportunity,
} from './api'
import { defaultFilters, OpportunityFilterSet } from './filters'
import { deserializeFilters } from '@user/components/Filters'
import { loadFilterPreferences } from '../userPreferences/api'
import { CardListViewMode, toast } from '@sales-center/components'
import { pageSizeOptions } from '@user/components/xGrid'
import { setOrderBy, updateFilterState } from './helpers'
import {
  ENTITY_TYPES,
  IEntityContactDTO,
  updatePhoneValueSearch,
} from '@sales-center/common'
import { addEditEntityLocation, matchCreateNew } from '../entityLocations'
import {
  updateEntityClosingRequirements,
  updateScheduleRequirementSettings,
} from '../closingRequirements'

export const opportunityAdapter = createEntityAdapter<IOpportunity>()

export const initialState =
  opportunityAdapter.getInitialState<OpportunityPageState>({
    page: 1,
    perPage: 10,
    count: 0,
    loading: true,
    filterSet: defaultFilters,
    shouldFetchList: false,
    detail: null,
    mode: 'card',
    multipleServiceLocations: false,
  })

export const opportunitySlice = createSlice({
  name: 'opportunities',
  initialState,
  reducers: {
    clearOpportunityDetail: (state) => {
      state.detail = null
    },
    setOpportunityPage: (state, { payload: page }: PayloadAction<number>) => {
      state.page = page > 0 ? page : 1
      state.shouldFetchList = true
    },
    setOpportunitiesPerPage: (state, { payload }: PayloadAction<number>) => {
      state.page = 1
      state.perPage = payload
      state.shouldFetchList = true
    },
    setOpportunityPageMode: (
      state,
      { payload }: PayloadAction<CardListViewMode>,
    ) => {
      state.mode = payload
      state.page = 1
      if (payload === 'list') {
        state.perPage =
          state.perPage > pageSizeOptions[0]
            ? state.perPage
            : pageSizeOptions[0]
      } else {
        state.perPage = 10
      }
      state.shouldFetchList = true

      setOrderBy(state, state.filterSet.orderBy)

      opportunityAdapter.removeAll(state)
    },
    setOpportunityFilters: (
      state,
      { payload }: PayloadAction<OpportunityFilterSet>,
    ) => updateFilterState(state, payload),
    decrementOppServicesCount: (
      state,
      { payload }: PayloadAction<{ oppId: number; count?: number }>,
    ) => {
      const { count = 1 } = payload
      const selectedOpp = state.entities[payload.oppId]

      if (selectedOpp) {
        const currentCount = selectedOpp.servicesCount || 0

        const update: Update<IOpportunity> = {
          id: payload.oppId,
          changes: {
            servicesCount: Math.max(0, currentCount - count),
          },
        }
        opportunityAdapter.updateOne(state, update)
      }

      // Update opportunity detail
      if (!!state.detail && state.detail?.id == payload.oppId) {
        state.detail.servicesCount--
      }
    },
    incrementOppServicesCount: (
      state,
      { payload }: PayloadAction<{ oppId: number }>,
    ) => {
      const selectedOpp = state.entities[payload.oppId]

      if (selectedOpp) {
        const currentCount = selectedOpp.servicesCount || 0

        const update: Update<IOpportunity> = {
          id: payload.oppId,
          changes: {
            servicesCount: currentCount + 1,
          },
        }
        opportunityAdapter.updateOne(state, update)
      }

      // Update opportunity detail
      if (!!state.detail && state.detail?.id == payload.oppId) {
        state.detail.servicesCount++
      }
    },
    updateOppReferralIdOnCard: (
      state,
      {
        payload,
      }: PayloadAction<{ opportunityId: number; referralDiscountId: string }>,
    ) => {
      const update: Update<IOpportunity> = {
        id: payload.opportunityId,
        changes: {
          referralDiscountId: payload.referralDiscountId,
        },
      }
      opportunityAdapter.updateOne(state, update)
    },
    updateOpportunityAgreementsCount: (
      state,
      {
        payload,
      }: PayloadAction<{
        action: 'decrement' | 'increment'
        oppId: number
      }>,
    ) => {
      const { action, oppId } = payload
      const selectedOpp = state.entities[oppId]
      const value = action == 'increment' ? 1 : -1

      if (selectedOpp) {
        const closingRequirements = selectedOpp.closingRequirements

        const update: Update<IOpportunity> = {
          id: payload.oppId,
          changes: {
            closingRequirements: {
              ...closingRequirements,
              agreementsCount: closingRequirements.agreementsCount + value,
            },
          },
        }
        opportunityAdapter.updateOne(state, update)
      }

      // Update opportunity detail
      if (!!state.detail && state.detail?.id == payload.oppId) {
        state.detail.closingRequirements.agreementsCount += value
      }
    },
    setNextActionDate: (
      state,
      { payload }: PayloadAction<{ oppId: number; date: Date | null }>,
    ) => {
      const update: Update<IOpportunity> = {
        id: payload.oppId,
        changes: {
          nextActionDate: payload.date,
        },
      }
      if (state.detail) {
        state.detail.nextActionDate = payload.date
      }
      opportunityAdapter.updateOne(state, update)
    },
    bulkSetNextActionDate: (
      state,
      { payload }: PayloadAction<{ oppIds: number[]; date: Date | null }>,
    ) => {
      const updates: Update<IOpportunity>[] = []
      payload.oppIds.map((leadId) =>
        updates.push({
          id: leadId,
          changes: {
            nextActionDate: payload.date,
          },
        }),
      )
      opportunityAdapter.updateMany(state, updates)
    },
    setExpectedClosingDate: (
      state,
      { payload }: PayloadAction<{ oppId: number; date: Date | null }>,
    ) => {
      const update: Update<IOpportunity> = {
        id: payload.oppId,
        changes: {
          expectedClosingDate: payload.date,
        },
      }
      if (state.detail) {
        state.detail.expectedClosingDate = payload.date
      }
      opportunityAdapter.updateOne(state, update)
    },
    bulkSetExpectedClosingDate: (
      state,
      { payload }: PayloadAction<{ oppIds: number[]; date: Date | null }>,
    ) => {
      const updates: Update<IOpportunity>[] = []
      payload.oppIds.map((leadId) =>
        updates.push({
          id: leadId,
          changes: {
            expectedClosingDate: payload.date,
          },
        }),
      )
      opportunityAdapter.updateMany(state, updates)
    },
  },
  extraReducers: (builder) => {
    // ========== E X P E C T E D   C L O S I N G   D A T E ==========
    builder.addCase(
      updateOpportunityExpectedClosingDate.fulfilled,
      (state, { payload }) => {
        const update: Update<IOpportunity> = {
          id: payload.opportunityId,
          changes: {
            expectedClosingDate: payload.expectedClosingDate,
          },
        }
        opportunityAdapter.updateOne(state, update)

        // Update opportunity detail
        if (!!state.detail && state.detail?.id == payload.opportunityId) {
          state.detail.expectedClosingDate = payload.expectedClosingDate
        }
      },
    )

    // ========== N E X T   A C T I O N   D A T E ==========
    builder.addCase(
      updateOpportunityNextActionDate.fulfilled,
      (state, { payload }) => {
        const update: Update<IOpportunity> = {
          id: payload.opportunityId,
          changes: {
            nextActionDate: payload.nextActionDate,
          },
        }
        opportunityAdapter.updateOne(state, update)

        // Update opportunity detail
        if (!!state.detail && state.detail?.id == payload.opportunityId) {
          state.detail.nextActionDate = payload.nextActionDate
        }
      },
    )

    // ========== R E O P EN ==========
    builder.addCase(reOpenOpportunity.fulfilled, (state) => {
      state.shouldFetchList = true
    })

    // ========== S T A T U S ==========
    builder.addCase(updateOpportunityStatus.fulfilled, (state, { payload }) => {
      const update: Update<IOpportunity> = {
        id: payload.opportunityId,
        changes: {
          salesFunnelStageId: payload.salesFunnelStageId,
          closed: !!payload.closedDate,
          closedDate: payload.closedDate,
        },
      }
      opportunityAdapter.updateOne(state, update)
      state.shouldFetchList = true

      // Update opportunity detail
      if (!!state.detail && state.detail?.id == payload.opportunityId) {
        state.detail.salesFunnelStageId = payload.salesFunnelStageId
        state.detail.closedDate = payload.closedDate!
        state.detail.age = payload.age!
        state.detail.closedLostReason = payload.customReason
      }
    })

    builder.addCase(closeWinOpportunity.fulfilled, (state) => {
      state.shouldFetchList = true
    })

    builder.addCase(updateOpportunityInfo.fulfilled, (state, { payload }) => {
      const update: Update<IOpportunity> = {
        id: payload.id,
        changes: {
          salesTeamId: payload.salesTeamId,
          locationTypeId: payload.locationTypeId,
          campaignId: payload.campaignId,
          salesFunnelId: payload.salesFunnelId,
          assignee: payload.assignee,
          salesFunnelStageId: payload.salesFunnelStageId,
        },
      }

      opportunityAdapter.updateOne(state, update)

      state.shouldFetchList = true

      // Update lead detail
      if (!!state.detail && state.detail?.id == payload.id) {
        state.detail.assigneeId = payload.assigneeId
        state.detail.salesTeamId = payload.salesTeamId
        state.detail.locationTypeId = payload.locationTypeId
        state.detail.campaignId = payload.campaignId
        state.detail.salesFunnelId = payload.salesFunnelId
        state.detail.salesFunnelStageId = payload.salesFunnelStageId
      }
    })

    builder.addCase(getOpportunities.pending, (state) => {
      state.loading = true
      state.shouldFetchList = false
    })

    builder.addCase(getOpportunities.rejected, (state) => {
      state.loading = false
      state.count = 0
      state.page = 1
      opportunityAdapter.removeAll(state)
    })

    builder.addCase(getOpportunities.fulfilled, (state, { payload }) => {
      const { '@odata.count': count, value } = payload
      state.loading = false
      state.shouldFetchList = false
      if (count !== undefined) {
        state.count = count
      }
      // correct dates
      value.map((i) => {
        if (i.nextActionDate !== null)
          i.nextActionDate = new Date(i.nextActionDate)
        if (i.closedDate !== null) i.closedDate = new Date(i.closedDate)
        if (i.createdDate !== null) i.createdDate = new Date(i.createdDate)
        if (i.updatedDate !== null) i.updatedDate = new Date(i.updatedDate)
        if (i.expectedClosingDate !== null)
          i.expectedClosingDate = new Date(i.expectedClosingDate)
        if (i.primaryContact.phone !== null)
          i.primaryContact.phone = updatePhoneValueSearch(
            i.primaryContact.phone,
          )
      })
      opportunityAdapter.setAll(state, value)
    })

    builder.addCase(
      hasMultiServiceLocations.fulfilled,
      (state, { payload }) => {
        const { value } = payload

        state.multipleServiceLocations = value.length > 0
      },
    )

    builder.addCase(loadFilterPreferences.fulfilled, (state, { payload }) => {
      const oppPref = payload.find((p) => p.type === 'opp')
      updateFilterState(
        state,
        oppPref
          ? deserializeFilters<IOpportunity, OpportunityFilterSet>(
              defaultFilters,
              oppPref.filter,
            )
          : defaultFilters,
      )
    })

    builder.addCase(matchCreateNew.fulfilled, (state, action) => {
      const {
        meta: { arg },
      } = action

      if (arg.entityType === ENTITY_TYPES.Opportunity && !arg.detail) {
        state.shouldFetchList = true
      }
    })

    builder.addCase(
      addEditEntityLocation.fulfilled,
      (state, { payload, meta }) => {
        const {
          arg: { entityType, entityId },
        } = meta

        if (entityType === ENTITY_TYPES.Opportunity) {
          const opp = opportunityAdapter
            .getSelectors()
            .selectById(state, entityId)

          if (opp?.primaryContactId === payload.contactId) {
            const contactName =
              payload.company !== null && payload.company.trim() !== ''
                ? payload.company
                : `${payload.firstName} ${payload.lastName}`

            const primaryContact: IEntityContactDTO = {
              externalLocationId: payload.externalLinkId ?? '',
              externalLocationCode: payload.locationCode ?? '',

              firstName: payload.firstName ?? '',
              lastName: payload.lastName ?? '',
              company: payload.company ?? '',
              contactName,

              emailAddresses: payload.emailAddresses,
              phone: payload.phoneNumber ?? '',
              phoneExt: payload.phoneExt ?? '',
              mobilePhone: '',

              address: payload.street1 ?? '',
              address2: payload.street2 ?? '',
              city: payload.city ?? '',
              state: payload.stateAbbreviation ?? '',
              postalCode: payload.postalCode ?? '',
              primaryEmailAddress: '',
            }

            opportunityAdapter.updateOne(state, {
              id: entityId,
              changes: {
                primaryContact,
              },
            })

            if (state.detail?.id === entityId) {
              state.detail.primaryContact = {
                ...state.detail.primaryContact,
                ...primaryContact,
              }
            }
          }
        }
      },
    )

    // ========== S A L E S  T E A M  ==========
    builder.addCase(
      updateReassignOpportunity.fulfilled,
      (state, { payload }) => {
        const update: Update<IOpportunity> = {
          id: payload.id,
          changes: {
            assignee: payload.assignee,
            salesTeamId: payload.salesTeamId,
          },
        }
        opportunityAdapter.updateOne(state, update)

        // Update opp detail
        if (!!state.detail && state.detail?.id == payload.id) {
          state.detail.assigneeId = payload.assigneeId
          state.detail.assignee = payload.assignee
          state.detail.salesTeamId = payload.salesTeamId
          state.detail.salesTeam = payload.salesTeamName
        }
      },
    )

    builder.addCase(deleteOpportunity.fulfilled, (state, { payload }) => {
      opportunityAdapter.removeOne(state, payload)
      state.count = state.count - 1
    })

    builder.addCase(getOpportunity.fulfilled, (state, { payload }) => {
      opportunityAdapter.upsertOne(state, payload)
    })

    builder.addCase(
      getOpportunityIsCloseable.fulfilled,
      (state, { payload }) => {
        const { id, isCloseable, closeRequirements } = payload

        opportunityAdapter.updateOne(state, {
          id,
          changes: {
            isCloseable,
            closeRequirements,
          },
        })

        if (id === state.detail?.id) {
          state.detail.closeRequirements = closeRequirements
        }
      },
    )

    builder.addCase(getOpportunityDetail.fulfilled, (state, { payload }) => {
      const { id, referralSummary, externalLocationCode, externalLocationId } =
        payload

      const update: Update<IOpportunity> = {
        id,
        changes: {
          referralDiscountCode: referralSummary?.referralDiscountCode,
          referralLocationId: referralSummary?.referralValue,
          referralDiscountId: referralSummary?.referralDiscountId,
          referralLocationCode: referralSummary?.referralLocationCode,
          referralValue: referralSummary?.referralValue,
          referralType: referralSummary?.referralType,
          externalLocationCode: externalLocationCode,
          externalLocationId: externalLocationId,
        },
      }

      opportunityAdapter.updateOne(state, update)
      state.detail = payload
      state.detail.primaryContact.altPhoneNumber = updatePhoneValueSearch(
        state.detail.primaryContact.altPhoneNumber ?? '',
      )
      state.detail.primaryContact.phoneNumber = updatePhoneValueSearch(
        state.detail.primaryContact.phoneNumber ?? '',
      )
      state.detail.billToContact.altPhoneNumber = updatePhoneValueSearch(
        state.detail.billToContact.altPhoneNumber ?? '',
      )
      state.detail.billToContact.phoneNumber = updatePhoneValueSearch(
        state.detail.billToContact.phoneNumber ?? '',
      )
      state.detail.serviceContact.altPhoneNumber = updatePhoneValueSearch(
        state.detail.serviceContact.altPhoneNumber ?? '',
      )
      state.detail.serviceContact.phoneNumber = updatePhoneValueSearch(
        state.detail.serviceContact.phoneNumber ?? '',
      )
      state.detail.secondaryContactsSummary.wwmContacts.map((c) => {
        c.contact.phoneNumber = updatePhoneValueSearch(
          c.contact.phoneNumber ?? '',
        )
        c.contact.altPhoneNumber = updatePhoneValueSearch(
          c.contact.altPhoneNumber ?? '',
        )
      })
    })

    builder.addCase(getOpportunityValue.fulfilled, (state, { payload }) => {
      const { value } = payload
      if (value.length > 0) {
        const {
          id,
          initialValue,
          recurringValue,
          totalValue,
          billToDivisionId,
          billToLocationTypeId,
          serviceLocation,
          primaryContact,
          hasValidLocationAddress,
          hasValidPropertyAddress,
        } = value[0]

        const update: Update<IOpportunity> = {
          id,
          changes: {
            initialValue,
            recurringValue,
            totalValue,
            billToLocationTypeId,
            billToDivisionId,
            serviceLocation,
            primaryContact,
            hasValidLocationAddress,
            hasValidPropertyAddress,
          },
        }

        opportunityAdapter.updateOne(state, update)

        const { detail } = state
        if (!!detail && detail.id === id) {
          detail.initialValue = initialValue
          detail.recurringValue = recurringValue
          detail.totalLeadValue = totalValue
        }
      }
    })
    builder.addCase(updateName.fulfilled, (state, { payload }) => {
      if (state.detail && state.detail.id === payload.id) {
        if (
          state.detail.opportunityName !== payload.name ||
          state.detail.useSystemName !== payload.useSystemName
        ) {
          state.detail.opportunityName = payload.name
          state.detail.useSystemName = payload.useSystemName
          state.shouldFetchList = true
        }
      }
    })

    builder.addCase(
      incrementOpportunityPaymentsCount.fulfilled,
      (state, { payload }) => {
        const oppId = payload
        const selectedOpp = state.entities[oppId]

        if (selectedOpp) {
          const closingRequirements = selectedOpp.closingRequirements

          const update: Update<IOpportunity> = {
            id: oppId,
            changes: {
              closingRequirements: {
                ...closingRequirements,
                paymentsCount: closingRequirements.paymentsCount + 1,
              },
            },
          }
          opportunityAdapter.updateOne(state, update)
        }

        // Update opportunity detail
        if (!!state.detail && state.detail?.id == oppId) {
          state.detail.closingRequirements.paymentsCount++
        }
      },
    )

    //#region bulk actions
    builder.addCase(bulkDeleteOpps.fulfilled, (state) => {
      state.shouldFetchList = true
    })
    builder.addCase(bulkUpdateNextActionDate.fulfilled, (state) => {
      state.shouldFetchList = true
    })
    builder.addCase(bulkUpdateOppStatus.fulfilled, (state, { payload }) => {
      state.shouldFetchList = payload.mode === 'commit'
    })
    builder.addCase(bulkReopenOpps.fulfilled, (state, { payload }) => {
      state.shouldFetchList = payload.mode === 'commit'
    })
    builder.addCase(bulkReassignOpps.fulfilled, (state, { payload }) => {
      state.shouldFetchList = payload.mode === 'commit'
      if (payload.mode === 'commit' && payload.model.draftAgreementsUpdated) {
        toast.success('Information has been updated on any draft agreements.')
      }
    })
    builder.addCase(
      bulkCloseWinOpportunities.fulfilled,
      (state, { payload }) => {
        state.shouldFetchList = payload.mode === 'commit'
      },
    )
    builder.addCase(
      bulkUpdateExpectedClosingDate.fulfilled,
      (state, { payload }) => {
        state.shouldFetchList = payload.mode === 'commit'
      },
    )
    //#endregion
    builder.addCase(createOpportunity.fulfilled, (state) => {
      state.shouldFetchList = true
    })

    builder.addCase(updateScheduleRequirementSettings.fulfilled, (state, _) => {
      state.shouldFetchList = true
    })

    builder.addCase(
      updateEntityClosingRequirements.fulfilled,
      (state, { payload, meta }) => {
        const { entityId, entityType } = meta.arg

        if (entityType === ENTITY_TYPES.Opportunity) {
          const { services, servicesCount, closingRequirements } = payload

          opportunityAdapter.updateOne(state, {
            id: entityId,
            changes: {
              services,
              servicesCount,
              closingRequirements,
            },
          })

          if (state.detail && state.detail?.id === entityId) {
            state.detail.closingRequirements = {
              servicesCount,
              appointments: services,
              ...closingRequirements,
            }
          }
        }
      },
    )

    builder.addCase(splitLeadOrOpportunity.rejected, (_state, { payload }) => {
      if (payload && payload.errors) {
        toast.error(payload.errors.message?.[0])
      }
    })
  },
})

export default opportunitySlice.reducer

export const {
  clearOpportunityDetail,
  setOpportunityPage,
  setOpportunitiesPerPage,
  setOpportunityPageMode,
  setOpportunityFilters,
  decrementOppServicesCount,
  incrementOppServicesCount,
  updateOpportunityAgreementsCount,
  updateOppReferralIdOnCard,
  setNextActionDate,
  bulkSetNextActionDate,
  setExpectedClosingDate,
  bulkSetExpectedClosingDate,
} = opportunitySlice.actions

export const opportunityAdapterSelectors =
  opportunityAdapter.getSelectors<StoreData>(
    (state) => state.opportunitiesState,
  )

export const {
  selectById: selectOpportunityById,
  selectIds: selectOpportunityIds,
  selectEntities: selectOpportunityEntities,
  selectAll: selectAllOpportunities,
  selectTotal: selectTotalOpportunities,
} = opportunityAdapterSelectors

export const selectOpportunityDetail = (state: StoreData) => {
  return state.opportunitiesState.detail
}

export const selectOpportunityPageState = (state: StoreData) => {
  const {
    opportunitiesState: {
      page,
      perPage,
      count,
      loading,
      shouldFetchList,
      mode,
      multipleServiceLocations,
    },
  } = state
  return {
    page,
    pages: Math.ceil(count / perPage),
    perPage,
    count,
    loading,
    shouldFetchList,
    mode,
    multipleServiceLocations,
  }
}

export const selectOpportunityFilters = (state: StoreData) => {
  const {
    opportunitiesState: { filterSet },
  } = state
  return { filterSet }
}

export const selectOppCloseWinErrorsById = (state: StoreData, id: number) => {
  const opp = selectOpportunityById(state, id)
  const detail = selectOpportunityDetail(state)

  if (opp) return opp.closeRequirements
  else if (id === detail?.id) {
    return detail.closeRequirements
  }
}
